/**
 * iter314 — Boutons "Ajouter à Apple Wallet / Google Wallet" pour le QR Practice
 *
 * - Appelle /api/portal/practice/wallet/status pour savoir quelles plateformes sont actives
 * - Télécharge le .pkpass en Blob (iOS Safari gère nativement application/vnd.apple.pkpass)
 * - Redirige vers l'URL signée Save-to-Google-Wallet pour Android/web
 * - Mode dégradé : boutons grisés "Configuration en cours" si credentials non fournis
 *
 * Usage: <WalletButtonsPractice />  (le token portail est lu depuis localStorage)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wallet, Smartphone, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function tokenHeaders() {
  const t = localStorage.getItem('client_portal_token') || localStorage.getItem('client_token') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function WalletButtonsPractice({ compact = false }) {
  const [status, setStatus] = useState(null); // { apple: bool, google: bool } | null
  const [busy, setBusy] = useState(null); // 'apple' | 'google' | null

  useEffect(() => {
    axios
      .get(`${API}/api/portal/practice/wallet/status`, { headers: tokenHeaders() })
      .then((r) => setStatus(r.data || { apple: false, google: false }))
      .catch(() => setStatus({ apple: false, google: false }));
  }, []);

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);

  const addApple = () => {
    if (!status?.apple) {
      toast.info('Apple Wallet en cours de configuration — réessayez plus tard.');
      return;
    }
    setBusy('apple');
    const t = localStorage.getItem('client_portal_token') || localStorage.getItem('client_token') || localStorage.getItem('token');
    axios
      .get(`${API}/api/portal/practice/wallet/apple.pkpass`, {
        headers: { Authorization: `Bearer ${t}` },
        responseType: 'blob',
      })
      .then((res) => {
        const blob = new Blob([res.data], { type: 'application/vnd.apple.pkpass' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'kempferhof.pkpass';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      })
      .catch((e) => {
        const msg = e?.response?.status === 503
          ? 'Apple Wallet en cours de configuration.'
          : 'Impossible de générer le pass Apple Wallet.';
        toast.error(msg);
      })
      .finally(() => setBusy(null));
  };

  const addGoogle = async () => {
    if (!status?.google) {
      toast.info('Google Wallet en cours de configuration — réessayez plus tard.');
      return;
    }
    setBusy('google');
    try {
      const { data } = await axios.get(`${API}/api/portal/practice/wallet/google`, { headers: tokenHeaders() });
      if (data?.configured && data.url) {
        window.location.href = data.url;
      } else {
        toast.info('Google Wallet en cours de configuration.');
      }
    } catch (_) {
      toast.error('Impossible de générer le pass Google Wallet.');
    } finally {
      setBusy(null);
    }
  };

  if (!status) return null;

  // Si rien n'est configuré, afficher un message discret et des boutons grisés "preview"
  const nothingConfigured = !status.apple && !status.google;

  return (
    <div className={`${compact ? 'flex flex-col gap-1.5' : 'flex flex-col sm:flex-row gap-2'} w-full`} data-testid="wallet-buttons-practice">
      {nothingConfigured && (
        <div className="text-[10px] text-neutral-400 italic text-center sm:text-left" data-testid="wallet-not-configured">
          Apple Wallet / Google Wallet — bientôt disponible
        </div>
      )}
      <button
        onClick={addApple}
        disabled={!status.apple || busy === 'apple'}
        className={`flex-1 inline-flex items-center justify-center gap-1.5 ${isIOS ? 'order-1' : 'order-2'} ${
          status.apple
            ? 'bg-black hover:bg-neutral-800 text-white'
            : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
        } ${compact ? 'text-[11px] py-1.5 px-2.5' : 'text-xs py-2.5 px-3'} font-semibold rounded-lg transition`}
        data-testid="add-to-apple-wallet-btn"
        title={status.apple ? 'Ajouter à Apple Wallet' : 'Configuration en cours'}
      >
        {busy === 'apple' ? <Loader2 size={12} className="animate-spin" /> : <Smartphone size={12} />}
        <span>Apple Wallet</span>
      </button>
      <button
        onClick={addGoogle}
        disabled={!status.google || busy === 'google'}
        className={`flex-1 inline-flex items-center justify-center gap-1.5 ${isAndroid ? 'order-1' : 'order-2'} ${
          status.google
            ? 'bg-[#4285F4] hover:bg-[#3B78DE] text-white'
            : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
        } ${compact ? 'text-[11px] py-1.5 px-2.5' : 'text-xs py-2.5 px-3'} font-semibold rounded-lg transition`}
        data-testid="add-to-google-wallet-btn"
        title={status.google ? 'Ajouter à Google Wallet' : 'Configuration en cours'}
      >
        {busy === 'google' ? <Loader2 size={12} className="animate-spin" /> : <Wallet size={12} />}
        <span>Google Wallet</span>
      </button>
    </div>
  );
}
